import { AllForecastComponents } from "../../app/forecastSurface";
import {
    BAD_WEATHER,
    GOLDILOCKS,
    GOOD_WEATHER,
    LEVEL_FIVE,
    LEVEL_FOUR,
    LEVEL_ONE,
    LEVEL_THREE,
    LEVEL_TWO,
    SurfaceEnum
} from "../UserVisibleStrings";
import { impactLevelInfo } from "../../app/riskLevel";
import { AnyLayer } from "mapbox-gl";
import { LineString } from "geojson";
import { RiskChangePosition, RoutePoint, RouteSegmentPointsList } from "../../generated/gpb/api/route";
import { createRouteLayer } from "./GeoViewStyle";
import { Layer, Source } from "react-map-gl";


export function getComponentNames( allComponents: AllForecastComponents | undefined ): Array<SurfaceEnum>
{
    if ( allComponents === undefined ) return [];
    const components = allComponents.impact;
    return components.components.map( ( c ) => c.name );
}

export function getDisabledComponentNames( allComponents: AllForecastComponents | undefined ): Array<SurfaceEnum>
{
    if ( allComponents === undefined ) return [];
    const components = allComponents.impact;
    return components.disabledComponentNames;
    // disabledComponentNames is the list of components that were specified in the weather config, but for which
    // there was no geojson generated (means either an error occurred OR there is no risk present across all taus)
}

export function lerp( a: number, b: number, alpha: number )
{
    return a + alpha * ( b - a );
}

export const logoHeight = 50;
export const logoWidth = 199;

export enum ZIndexLevels
    {
    MapIcon = 0,
    RVIcon = 1,
    UIElement = 2,
    BeneathDrawerUiElement = 800,
    TopUIElement = 1000,
    LoadingSurface = 900,
    // Anything above ~1250 or so will show above a greyed out screen, so if we are using modals anywhere large z-indexes might look awkward.
}

export const MS_IN_DAY = 24 * 60 * 60 * 1000;

/**
 * Calculate the start of the day for a given timestamp.
 *
 * This function takes a timestamp, calculates the next day's start time,
 * and returns the start of the current day in milliseconds.
 *
 * @param {number} time - The timestamp in milliseconds.
 * @returns {number} The start of the day in milliseconds.
 */
export function getDayStart( time: number )
{
    const firstDayChange = new Date( time );
    firstDayChange.setHours( 24, 0, 0, 0 );
    const minDay = firstDayChange.getTime() - MS_IN_DAY;
    return minDay;
}

// Define a type for determining whether certain layers are enabled
// NOTE: keys must match risk level names as defined in riskLevelInfo
export interface LayerVisibility
{
    Great: boolean;
    Slight: boolean;
    Moderate: boolean;
    Challenging: boolean;
    Hazardous: boolean;
    Treacherous: boolean;
    Route: boolean;
    IconLegend: boolean;
}

export const ROUTE_DISPLAY_TYPES = {
    ROUTE: "route",
    POST: "post",
    OUTSIDE: "outside",
    GOLDILOCKS: "goldilocks",
    NO_IMPACT: "noimpact",
    LEVEL_ONE: "levelone",
    LEVEL_TWO: "leveltwo",
    LEVEL_THREE: "levelthree",
    LEVEL_FOUR: "levelfour",
    LEVEL_FIVE: "levelfive",
} as const;

export type RouteDisplayType = typeof ROUTE_DISPLAY_TYPES[keyof typeof ROUTE_DISPLAY_TYPES];


export function getImpactLayerConfig( selectedChip: string, opacity: number, isLayerEnabledTracker: LayerVisibility, id: string ): AnyLayer
{
    // special case: the surface needs to specifically include or exclude goldilocks as we fetch both for any aggregate request
    const showImpact = selectedChip !== GOOD_WEATHER;
    const showGoldilocks = selectedChip !== BAD_WEATHER;
    return {
        id: id,
        type: "fill",
        layout: { visibility: "visible" },
        paint: {
            "fill-outline-color": "rgba(0, 0, 0, 0)",
            "fill-opacity": [
                "match",
                [ "get", "severity" ],
                "LEVEL_ONE",
                isLayerEnabledTracker.Slight && showImpact ? opacity : 0,
                "LEVEL_TWO",
                isLayerEnabledTracker.Moderate && showImpact ? opacity : 0,
                "LEVEL_THREE",
                isLayerEnabledTracker.Challenging && showImpact ? opacity : 0,
                "LEVEL_FOUR",
                isLayerEnabledTracker.Hazardous && showImpact ? opacity : 0,
                "GOLDILOCKS",
                isLayerEnabledTracker.Great && showGoldilocks ? opacity : 0,
                /* Default to high */
                isLayerEnabledTracker.Treacherous && showImpact ? opacity : 0,
            ],
            "fill-color": [
                "match",
                [ "get", "severity" ],
                "LEVEL_ONE",
                impactLevelInfo[LEVEL_ONE].color,
                "LEVEL_TWO",
                impactLevelInfo[LEVEL_TWO].color,
                "LEVEL_THREE",
                impactLevelInfo[LEVEL_THREE].color,
                "LEVEL_FOUR",
                impactLevelInfo[LEVEL_FOUR].color,
                "GOLDILOCKS",
                impactLevelInfo[GOLDILOCKS].color,
                /* Default to high */
                impactLevelInfo[LEVEL_FIVE].color,
            ],
        },
    };
}

/**
 * Calculate the left placement for the logo
 *
 * This function ensures the logo stays aligned with the middle of the timeline when
 * the left and right drawer are open.
 */
export const calculateLogoLeft = ( isMobile: boolean, isRightBarExpanded: boolean, isLeftBarExpanded: boolean ) =>
{
    if ( isMobile ) return "10px";
    if ( isRightBarExpanded ) return "calc(50% - 120px)";
    if ( isLeftBarExpanded ) return "calc(50% + 200px)";
    return "50%";
};

const MS_TO_MINUTE = 1000 * 60;
const MS_TO_DAY = 1000 * 60 * 60 * 24;
export function formatPitStopTime( startTime: number | undefined, endTime: number | undefined )
{
    if ( endTime === undefined || startTime === undefined )
    {
        return "unknown length";
    }
    const pitStopMinutes = Math.round( ( endTime - startTime ) / MS_TO_MINUTE );

    return `${pitStopMinutes} Minute${pitStopMinutes === 1 ? "" : "s"}`;
}

export function formatOvernightTime( startTime: number | undefined, endTime: number | undefined )
{
    if ( endTime === undefined || startTime === undefined )
    {
        return "unknown length";
    }
    const overnightDays = Math.round( ( endTime - startTime ) / MS_TO_DAY );

    //some overnight stays could round to less than 1 night, so we default to minimum 1 night.
    if ( overnightDays === 0 )
    {
        return "1 Night";
    }

    return `${overnightDays} Night${overnightDays === 1 ? "" : "s"}`;
}

/**
 * Generate points from route segments.
 * @param {RoutePoints} routePoints - The route points data.
 * @returns {Point[] | null} Array of points or null if no segments are available.
 */
export const generatePoints = ( routePoints: RouteSegmentPointsList ) => routePoints !== undefined && routePoints.segments.length > 0
    ? routePoints.segments.flatMap( segment => segment.points )
    : null;

export const generateRiskChangeList = ( routePoints: RouteSegmentPointsList ): Array<RiskChangePosition> | undefined =>
{
    const segmentsAtIndex: Array<number> = [ 0 ];
    routePoints.segments.reduce( ( prev, curr, i ) => segmentsAtIndex[i + 1] = prev + curr.points.length, 0 );
    return routePoints !== undefined && routePoints.segments.length > 0
        ? routePoints.segments.flatMap( ( segment, index ) => segment.riskChanges.map( ( riskChangePosition ) => ( {
            index: riskChangePosition.index + segmentsAtIndex[index],
            risk: riskChangePosition.risk
        } ) ) )
        : undefined;
};

/**
 * Generate a Point geometry based on the selected time.
 * @param {Point[] | null} points - Array of points.
 * @param {number} selectedTime - The selected timestamp.
 * @returns {PointGeometry | undefined} Point geometry or undefined if no points are available.
 */
export const generatePoint = ( points: Array<RoutePoint> | null, selectedTime: number ) =>
{
    if ( !points || points.length < 2 ) return undefined;

    if ( points[0].timeStamp !== undefined && selectedTime <= points[0].timeStamp.getTime() )
    {
        return {
            type: "Point",
            coordinates: [ points[0].longitude, points[0].latitude ],
        };
    }

    const lastPoint = points[points.length - 1];
    if ( lastPoint.timeStamp !== undefined && selectedTime >= lastPoint.timeStamp.getTime() )
    {
        return {
            type: "Point",
            coordinates: [ lastPoint.longitude, lastPoint.latitude ],
        };
    }

    for ( let i = 0; i < points.length - 1; i++ )
    {
        const currentPoint = points[i];
        const nextPoint = points[i + 1];

        if ( currentPoint.timeStamp !== undefined && nextPoint.timeStamp !== undefined )
        {
            const startTime = currentPoint.timeStamp.getTime();
            const endTime = nextPoint.timeStamp.getTime();

            if ( startTime < selectedTime && endTime > selectedTime )
            {
                const alpha = ( selectedTime - startTime ) / ( endTime - startTime );
                return {
                    type: "Point",
                    coordinates: [
                        lerp( currentPoint.longitude, nextPoint.longitude, alpha ),
                        lerp( currentPoint.latitude, nextPoint.latitude, alpha ),
                    ],
                };
            }
        }
    }
};

export enum PointCategory
    {
    inForecast = 1,
    postForecast = 2,
    outsideForecast = 3,
    goldilocks = 4,
    none = 5,
    levelOne = 6,
    levelTwo = 7,
    levelThree = 8,
    levelFour = 9,
    levelFive = 10,
}

export function addPointToRoute(
    lastObservedPointCategory: PointCategory | undefined,
    pointCategoryObserved: PointCategory,
    arrayToUpdate: Array<LineString>,
    point: RoutePoint,
    allCategoryArrays: Record<PointCategory, Array<LineString>>
): PointCategory
{
    const coord = [ point.longitude, point.latitude ] as [number, number];

    if ( lastObservedPointCategory === pointCategoryObserved )
    {
        //same category, just add to current LineString
        arrayToUpdate[arrayToUpdate.length - 1]?.coordinates.push( coord );
    }
    else
    {
        //different category so:
        // 1. Push to the new category
        arrayToUpdate.push( {
            type: "LineString",
            coordinates: [ coord ]
        } );

        // 2. Also push to the last category if it exists
        if ( lastObservedPointCategory !== undefined )
        {
            const lastArray = allCategoryArrays[lastObservedPointCategory];
            if ( lastArray !== undefined && lastArray.length > 0 )
            {
                lastArray[lastArray.length - 1].coordinates.push( coord );
            }
        }
    }

    return pointCategoryObserved;
}

export const renderRouteSources = (
    routeType: RouteDisplayType,
    routes: Array<LineString>,
    routeNumber: number,
    isSelected: boolean,
    lineOpacity: number,
) =>
{
    return routes.map( ( route, index ) => (
        <Source
            key = {`route-source-${routeType}-${routeNumber}-${index}`}
            id = {`route-data-${routeType}-${routeNumber}-${index}`}
            type = "geojson"
            data = {route}
        >
            <Layer {...createRouteLayer( routeType, routeNumber, isSelected, index, lineOpacity )}/>
        </Source>
    ) );
};

